"use client";
import { useState } from "react";
import { FileExtensionsWithRoutesAndIcons, Toast } from "../Common";
import ViewerWrapper from "./RendererUploadView/ViewerWrapper";
import UploadSection from "./Upload/UploadSection";
import axios from "axios";

interface DetailWrapperProps {
  usecase: string;
}

interface SelectedDocumentType {
  fileName: string;
  fileUrl: string;
  fileQuestions: string[] | string;
}

const DetailWrapper = ({ usecase }: DetailWrapperProps) => {
  const [enableRenderingView, setEnableRenderingView] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [selectedDocument, setSelectedDocument] =
    useState<SelectedDocumentType>({
      fileName: "",
      fileUrl: "",
      fileQuestions: [],
    });

  const handleBackToUpload = () => {
    setEnableRenderingView(false);
    setSelectedDocument({
      fileName: "",
      fileUrl: "",
      fileQuestions: [],
    });
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const matchedExtension = FileExtensionsWithRoutesAndIcons?.find(
    (extension) => extension?.route === usecase,
  );

  const createPdfUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  const onFileUpload = async (file: File) => {
    if (!file) return;
    if (file.size >= 200_000_000) {
      Toast({ message: "File size should be less than 200MB" });
      return;
    }

    try {
      setIsUploadingDocument(true);

      const formData = new FormData();
      formData.append("file", file);
      setUploadedFile(file);

      const fileUrl = createPdfUrl(file);
      setPdfUrl(fileUrl);

      const response = await axios.put(
        process.env.NEXT_PUBLIC_AI_TOOLS_URL! + "/pdf-summarize",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const summaryData = response.data;
      let summaryText = "";
      if (typeof summaryData === "string") {
        summaryText = summaryData;
      } else if (summaryData?.summary) {
        summaryText = summaryData.summary;
      } else {
        summaryText = "No summary found.";
      }

      setSelectedDocument({
        fileName: file.name,
        fileUrl: fileUrl,
        fileQuestions: summaryText,
      });

      setEnableRenderingView(true);
    } catch (error) {
      console.error("Summarization error:", error);
      let errorMsg = "PDF summarization failed!";
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error instanceof Error && error.message) {
        errorMsg = error.message;
      }
      alert(errorMsg);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  return (
    <div
      className={`mb-2 mt-4 ${enableRenderingView && "relative h-[calc(100vh-10rem)]"}`}
    >
      <div className="absolute bottom-[-2%] left-[-2%] right-[-2%] top-[20%] z-[-1] rounded-[400px] bg-[radial-gradient(circle,_#97aeff,_#97aeff)] opacity-35 blur-3xl" />
      {enableRenderingView ? (
        <div className="h-full bg-[#F2F4F7]">
          <div className="h-full px-4 py-4 xl:px-12 xl:py-8">
            <ViewerWrapper
              selectedDocument={pdfUrl}
              onBack={handleBackToUpload}
              pdfSummary={selectedDocument.fileQuestions}
              fileName={selectedDocument.fileName}
            />
          </div>
        </div>
      ) : (
        <UploadSection
          onFileUpload={onFileUpload}
          detailData={matchedExtension}
          isUploading={isUploadingDocument}
        />
      )}
    </div>
  );
};

export default DetailWrapper;
